/**
 * TeamPage — public team display + admin CRUD.
 * data-testid: candeza-team-*
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { PageContainer } from '@/components/layout';
import { Button } from '@/components/common';
import { useAuth } from '@/context';
import type { Role } from '@/types';
import {
  useInstructors,
  useAllInstructors,
  useManagement,
  useAllManagement,
  useCreateTeamMember,
  useUpdateTeamMember,
  useDeleteTeamMember,
  type TeamMember,
} from './api';

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const MemberCard = styled.div<{ $hidden?: boolean }>`
  border: 1px solid ${({ $hidden, theme }) => $hidden ? '#fbbf24' : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  background: ${({ $hidden, theme }) => $hidden ? '#fffbeb' : theme.colors.surface};
`;

const MemberImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MemberName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const MemberRole = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  padding-left: ${({ theme }) => theme.spacing.sm};
`;

const AdminActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
`;

const HiddenBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
`;

const Loading = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  padding: ${({ theme }) => theme.spacing.lg};
`;

// ---------------------------------------------------------------------------
// Simple inline add-member form
// ---------------------------------------------------------------------------

interface AddMemberFormProps {
  collection: 'instructors' | 'management';
  onClose: () => void;
}

const FormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 400px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FormTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.label`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
`;

const FormInput = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`;

const AddMemberForm: React.FC<AddMemberFormProps> = ({ collection, onClose }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [tagline, setTagline] = useState('');
  const [order, setOrder] = useState(0);
  const createMutation = useCreateTeamMember(collection);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({ name, role, tagline, order });
      toast.success('Team member added');
      onClose();
    } catch {
      toast.error('Failed to add team member (super-admin access required)');
    }
  };

  return (
    <FormOverlay>
      <FormCard data-testid="candeza-team-add-member-modal">
        <FormTitle>Add {collection === 'instructors' ? 'Instructor' : 'Management Member'}</FormTitle>
        <form onSubmit={handleSubmit}>
          <FormField>
            <FormLabel>Name *</FormLabel>
            <FormInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="candeza-team-form-name"
            />
          </FormField>
          <FormField>
            <FormLabel>Role *</FormLabel>
            <FormInput
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
              data-testid="candeza-team-form-role"
            />
          </FormField>
          <FormField>
            <FormLabel>Tagline</FormLabel>
            <FormInput
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              data-testid="candeza-team-form-tagline"
            />
          </FormField>
          <FormField>
            <FormLabel>Display Order</FormLabel>
            <FormInput
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              data-testid="candeza-team-form-order"
            />
          </FormField>
          <FormActions style={{ marginTop: 16 }}>
            <Button type="button" variant="outline" size="sm" onClick={onClose} data-testid="candeza-team-form-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={createMutation.isPending}
              data-testid="candeza-team-form-save"
            >
              {createMutation.isPending ? 'Adding…' : 'Add Member'}
            </Button>
          </FormActions>
        </form>
      </FormCard>
    </FormOverlay>
  );
};

// ---------------------------------------------------------------------------
// Main TeamPage
// ---------------------------------------------------------------------------

export const TeamPage: React.FC = () => {
  const { user } = useAuth();
  const role = (user?.role ?? '') as Role;
  const isAdmin = role === 'admin' || role === 'owner';

  const [showAddInstructor, setShowAddInstructor] = useState(false);
  const [showAddManagement, setShowAddManagement] = useState(false);

  // Public hooks show hidden=false. Admin hooks show all.
  const allInstrQuery = useAllInstructors();
  const pubInstrQuery = useInstructors();
  const allMgmtQuery = useAllManagement();
  const pubMgmtQuery = useManagement();
  const instrQuery = isAdmin ? allInstrQuery : pubInstrQuery;
  const mgmtQuery = isAdmin ? allMgmtQuery : pubMgmtQuery;

  const deleteInstructorMutation = useDeleteTeamMember('instructors');
  const deleteMgmtMutation = useDeleteTeamMember('management');
  const updateInstrMutation = useUpdateTeamMember('instructors');
  const updateMgmtMutation = useUpdateTeamMember('management');

  const handleToggleHide = async (member: TeamMember, col: 'instructors' | 'management') => {
    const updateMut = col === 'instructors' ? updateInstrMutation : updateMgmtMutation;
    try {
      await updateMut.mutateAsync({ id: member.id, data: { hidden: !member.hidden } });
      toast.success(member.hidden ? 'Member visible' : 'Member hidden');
    } catch {
      toast.error('Failed to update (super-admin required)');
    }
  };

  const handleDelete = async (id: string, col: 'instructors' | 'management') => {
    if (!confirm('Delete this team member? This cannot be undone.')) return;
    try {
      if (col === 'instructors') await deleteInstructorMutation.mutateAsync(id);
      else await deleteMgmtMutation.mutateAsync(id);
      toast.success('Team member deleted');
    } catch {
      toast.error('Failed to delete (super-admin required)');
    }
  };

  return (
    <PageContainer
      title="Our Team"
      subtitle="Meet the instructors and management at Rhythm and Beats"
      data-testid="candeza-team-page"
    >
      {/* Instructors */}
      <section style={{ marginBottom: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionTitle data-testid="candeza-team-instructors-title">Instructors</SectionTitle>
          {isAdmin && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddInstructor(true)}
              data-testid="candeza-team-add-instructor-btn"
            >
              + Add Instructor
            </Button>
          )}
        </div>
        {instrQuery.isLoading ? (
          <Loading data-testid="candeza-team-instructors-loading">Loading instructors…</Loading>
        ) : !instrQuery.data?.length ? (
          <Empty data-testid="candeza-team-instructors-empty">No instructors found.</Empty>
        ) : (
          <Grid data-testid="candeza-team-instructors-grid">
            {instrQuery.data.map((m) => (
              <MemberCard key={m.id} $hidden={m.hidden} data-testid={`candeza-team-instructor-row-${m.id}`}>
                {m.image && (
                  <MemberImage
                    src={m.image}
                    alt={m.name}
                    data-testid={`candeza-team-instructor-img-${m.id}`}
                  />
                )}
                <MemberName data-testid={`candeza-team-instructor-name-${m.id}`}>{m.name}</MemberName>
                <MemberRole>{m.role}</MemberRole>
                {m.tagline && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{m.tagline}</div>
                )}
                {m.hidden && <HiddenBadge>Hidden</HiddenBadge>}
                {isAdmin && (
                  <AdminActions>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleHide(m, 'instructors')}
                      data-testid={`candeza-team-instructor-toggle-${m.id}`}
                    >
                      {m.hidden ? 'Show' : 'Hide'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(m.id, 'instructors')}
                      data-testid={`candeza-team-instructor-delete-${m.id}`}
                    >
                      Delete
                    </Button>
                  </AdminActions>
                )}
              </MemberCard>
            ))}
          </Grid>
        )}
      </section>

      {/* Management */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SectionTitle data-testid="candeza-team-management-title">Management</SectionTitle>
          {isAdmin && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddManagement(true)}
              data-testid="candeza-team-add-management-btn"
            >
              + Add Member
            </Button>
          )}
        </div>
        {mgmtQuery.isLoading ? (
          <Loading data-testid="candeza-team-management-loading">Loading management…</Loading>
        ) : !mgmtQuery.data?.length ? (
          <Empty data-testid="candeza-team-management-empty">No management members found.</Empty>
        ) : (
          <Grid data-testid="candeza-team-management-grid">
            {mgmtQuery.data.map((m) => (
              <MemberCard key={m.id} $hidden={m.hidden} data-testid={`candeza-team-management-row-${m.id}`}>
                {m.image && <MemberImage src={m.image} alt={m.name} />}
                <MemberName data-testid={`candeza-team-management-name-${m.id}`}>{m.name}</MemberName>
                <MemberRole>{m.role}</MemberRole>
                {m.hidden && <HiddenBadge>Hidden</HiddenBadge>}
                {isAdmin && (
                  <AdminActions>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleHide(m, 'management')}
                      data-testid={`candeza-team-management-toggle-${m.id}`}
                    >
                      {m.hidden ? 'Show' : 'Hide'}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(m.id, 'management')}
                      data-testid={`candeza-team-management-delete-${m.id}`}
                    >
                      Delete
                    </Button>
                  </AdminActions>
                )}
              </MemberCard>
            ))}
          </Grid>
        )}
      </section>

      {showAddInstructor && (
        <AddMemberForm collection="instructors" onClose={() => setShowAddInstructor(false)} />
      )}
      {showAddManagement && (
        <AddMemberForm collection="management" onClose={() => setShowAddManagement(false)} />
      )}
    </PageContainer>
  );
};

